import { record } from "rrweb";
import { sanitizeRecordedEvent } from "./privacy.js";

export type FormResultOutcome = "success" | "rejected" | "error";
export type PortalRequestAction = "viewed" | "completed";

export type OrbitTrackerOptions = {
  /** Base URL of the Orbit API. */
  endpoint: string;
  /** Site API key from Orbit. */
  apiKey: string;
  /**
   * Required site label shown/filtered in the dashboard
   * (e.g. "carpow.co.nz", "apply.carpow.co.nz").
   */
  site: string;
  /**
   * Optional form id (e.g. "car-loan-application").
   * When set, `page` is treated as an integer form step and should be updated
   * with setPage() as the visitor progresses.
   * Can also be attached later via setForm() when adopting a parent session.
   */
  form?: string;
  /**
   * Starting form step (integer). Defaults to 1 when `form` is set.
   * Ignored when `form` is not provided.
   */
  page?: number;
  /**
   * Adopt an existing server session (e.g. parent page handoff into a form iframe).
   * Same Orbit API key must own that session. On success, continues recording into it
   * and attaches `form`/`page` when provided.
   */
  sessionId?: string;
  visitorId?: string;
  flushIntervalMs?: number;
  maxBatchSize?: number;
  maskAllInputs?: boolean;
  /**
   * When true (with maskAllInputs), only mask password fields and inputs
   * marked with data-orbit-masked="true". All other inputs are recorded.
   */
  maskOnlyMarkedInputs?: boolean;
  /** Custom rrweb input mask. Overrides maskOnlyMarkedInputs when set. */
  maskInputFn?: (text: string, element?: HTMLElement) => string;
  onSessionStart?: (sessionId: string) => void;
  onError?: (error: unknown) => void;
  recordCrossOriginIframes?: boolean;
};

export type OrbitTrackerHandle = {
  sessionId: string | null;
  /**
   * Stop local recording. Pass `{ endSession: true }` to also end the server
   * session and clear sessionStorage (default is flush-only so refresh can resume).
   */
  stop: (opts?: { endSession?: boolean }) => void;
  flush: () => Promise<void>;
  /**
   * Attach or switch form on the current session (parent → form handoff).
   */
  setForm: (
    formId: string,
    page?: number,
    extra?: { url?: string },
  ) => Promise<void>;
  /**
   * Update the current form step (integer). Requires `form` at init or setForm().
   */
  setPage: (page: number, extra?: { url?: string }) => Promise<void>;
  /**
   * Report form submission outcome: success | rejected | error.
   */
  trackFormResult: (
    outcome: FormResultOutcome,
    detail?: string,
  ) => Promise<void>;
  /** Generic custom event (also stored on the rrweb timeline). */
  trackEvent: (
    name: string,
    payload?: Record<string, unknown>,
  ) => Promise<void>;
  /**
   * Record a portal todo/request view or completion.
   * One session may contain multiple named requests.
   */
  trackPortalRequest: (
    name: string,
    action: PortalRequestAction,
  ) => Promise<void>;
};

declare global {
  interface Window {
    /** Global API attached by the IIFE/UMD build. Call `OrbitTracker.init(...)`. */
    OrbitTracker?: {
      init: (options: OrbitTrackerOptions) => OrbitTrackerHandle;
      /** Currently active tracker instance, if any. */
      getInstance: () => OrbitTrackerHandle | null;
    };
    /** Convenience alias set by `init()` to the active handle. */
    __orbitTracker?: OrbitTrackerHandle;
  }
}

const DEFAULT_FLUSH_MS = 4000;
const DEFAULT_BATCH = 40;
/** Chrome caps keepalive request bodies ~64KB; never use keepalive for event batches. */
const FETCH_TIMEOUT_MS = 30_000;
/** Split large rrweb batches so a single full-snapshot dump doesn't hang the request. */
const MAX_EVENTS_BODY_BYTES = 512 * 1024;

/** Active tracker for this page (single instance). */
let activeHandle: OrbitTrackerHandle | null = null;

/** Dev-friendly console breadcrumb (never throws if console is missing/restricted). */
function logInfo(...args: unknown[]): void {
  try {
    // eslint-disable-next-line no-console
    console.info("[OrbitTracker]", ...args);
  } catch {
    /* ignore */
  }
}

/**
 * Frame URL is always the document that loaded the tracker.
 * Top URL/origin are best-effort extras when same-origin with the parent.
 */
function captureWindowContext(): {
  url?: string;
  topUrl?: string;
  topOrigin?: string;
  inIframe: boolean;
} {
  const url = typeof location !== "undefined" ? location.href : undefined;
  let topUrl: string | undefined;
  let topOrigin: string | undefined;
  let inIframe = false;

  if (typeof window !== "undefined") {
    try {
      inIframe = window.top != null && window.top !== window.self;
    } catch {
      inIframe = true;
    }

    if (inIframe) {
      try {
        const topWin = window.top;
        if (topWin?.location) {
          topUrl = topWin.location.href || undefined;
          topOrigin = topWin.location.origin || undefined;
        }
      } catch {
        // Cross-origin parent — browser blocks access; leave top* unset.
      }
    }
  }

  return { url, topUrl, topOrigin, inIframe };
}

/**
 * sessionStorage key — cleared when the tab/window is closed, survives refresh.
 * Scoped by endpoint + apiKey + site only (not form) so form attach/handoff
 * continues the same local sid after parent → iframe journeys on same origin.
 */
function sessionStorageKey(parts: {
  endpoint: string;
  apiKey: string;
  site: string;
}): string {
  return `orbit_sid:${parts.endpoint}|${parts.apiKey}|${parts.site}`;
}

function readStoredSessionId(key: string): string | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    const v = sessionStorage.getItem(key);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

function writeStoredSessionId(key: string, id: string): void {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(key, id);
  } catch {
    /* private mode / quota */
  }
}

function clearStoredSessionId(key: string): void {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function isOrbitMaskedElement(element?: HTMLElement): boolean {
  let node: HTMLElement | null | undefined = element;
  while (node) {
    if (node.getAttribute?.("data-orbit-masked") === "true") return true;
    node = node.parentElement;
  }
  return false;
}

function resolveMaskInputFn(
  options: OrbitTrackerOptions,
): ((text: string, element?: HTMLElement) => string) | undefined {
  if (options.maskInputFn) return options.maskInputFn;
  if (!options.maskOnlyMarkedInputs) return undefined;
  return (text: string, element?: HTMLElement) => {
    const raw = String(text ?? "");
    const type = (element as HTMLInputElement | undefined)?.type;
    if (type === "password" || isOrbitMaskedElement(element)) {
      return "*".repeat(raw.length);
    }
    return raw;
  };
}

/**
 * Start session recording. Safe to call after the script is loaded
 * (script tag, dynamic import, or bundler import) — does nothing until this runs.
 *
 * Session id is stored in sessionStorage so a refresh resumes the same session.
 * A new session is created only when storage is empty or the server session is gone.
 * Re-calling stops local recording without ending the server session (SPA navigations).
 */
export function init(options: OrbitTrackerOptions): OrbitTrackerHandle {
  if (!options?.endpoint || !options?.apiKey) {
    throw new Error("OrbitTracker: endpoint and apiKey are required");
  }
  if (!options.site || !String(options.site).trim()) {
    throw new Error("OrbitTracker: site is required");
  }
  if (options.page != null && !options.form) {
    throw new Error("OrbitTracker: page requires form to be set");
  }

  logInfo("init", {
    endpoint: String(options.endpoint).replace(/\/$/, ""),
    site: String(options.site).trim(),
    form: options.form ? String(options.form).trim() : undefined,
    page: options.page,
    adoptSessionId: options.sessionId ? String(options.sessionId).slice(0, 8) : undefined,
  });

  // One active recorder per page load — don't end the server session on re-init.
  if (activeHandle) {
    try {
      activeHandle.stop();
    } catch {
      /* ignore */
    }
    activeHandle = null;
  }

  const endpoint = options.endpoint.replace(/\/$/, "");
  const flushIntervalMs = options.flushIntervalMs ?? DEFAULT_FLUSH_MS;
  const maxBatchSize = options.maxBatchSize ?? DEFAULT_BATCH;
  const site = String(options.site).trim();
  let form = options.form ? String(options.form).trim() : undefined;
  const adoptSessionId = options.sessionId?.trim() || undefined;
  const sidKey = sessionStorageKey({
    endpoint,
    apiKey: options.apiKey,
    site,
  });
  let currentPage =
    form != null
      ? Number.isFinite(options.page)
        ? Math.floor(Number(options.page))
        : 1
      : Number.isFinite(options.page)
        ? Math.floor(Number(options.page))
        : undefined;

  let sessionId: string | null = null;
  let buffer: unknown[] = [];
  let stopped = false;
  const maskedNodeIds = new Set<number>();
  let stopRecording: (() => void) | undefined;
  let flushTimer: ReturnType<typeof setInterval> | undefined;
  let flushing = false;

  let resolveSessionReady: () => void = () => undefined;
  const sessionReady: Promise<void> = new Promise((resolve) => {
    resolveSessionReady = resolve;
  });

  const headers = (): HeadersInit => ({
    "Content-Type": "application/json",
    "X-Orbit-Key": options.apiKey,
  });

  async function fetchJson(
    url: string,
    init: RequestInit & { timeoutMs?: number } = {},
  ): Promise<Response> {
    const { timeoutMs = FETCH_TIMEOUT_MS, ...rest } = init;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, {
        ...rest,
        credentials: "omit",
        // Do NOT set keepalive on large event posts — browsers limit ~64KB and
        // full rrweb snapshots often exceed that, leaving the request "pending".
        signal: controller.signal,
      });
    } finally {
      window.clearTimeout(timer);
    }
  }

  async function postEventChunk(events: unknown[]): Promise<void> {
    if (!sessionId || events.length === 0) return;
    const res = await fetchJson(`${endpoint}/api/ingest/sessions/${sessionId}/events`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ events }),
    });
    if (res.status === 404) {
      // Server session gone — drop storage so the next init creates a new one.
      clearStoredSessionId(sidKey);
      sessionId = null;
      throw new Error("OrbitTracker: session no longer exists on server");
    }
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`OrbitTracker: flush failed (${res.status}) ${text}`);
    }
  }

  /** Recursively split oversized batches (JSON length) into smaller POSTs. */
  async function postEvents(events: unknown[]): Promise<void> {
    if (events.length === 0) return;
    const body = JSON.stringify({ events });
    if (body.length > MAX_EVENTS_BODY_BYTES && events.length > 1) {
      const mid = Math.ceil(events.length / 2);
      await postEvents(events.slice(0, mid));
      await postEvents(events.slice(mid));
      return;
    }
    // Single event over the body cap (rrweb full-snapshot): drop, do not POST.
    if (body.length > MAX_EVENTS_BODY_BYTES) {
      logInfo("dropping oversized event", { bytes: body.length });
      return;
    }
    await postEventChunk(events);
  }

  async function createNewSession(): Promise<void> {
    const ctx = captureWindowContext();
    const body = {
      site,
      form,
      page: currentPage,
      // Frame document URL (where the tracker is running)
      url: ctx.url,
      // Extra: parent page when same-origin iframe
      topUrl: ctx.topUrl,
      topOrigin: ctx.topOrigin,
      screenWidth: typeof screen !== "undefined" ? screen.width : undefined,
      screenHeight: typeof screen !== "undefined" ? screen.height : undefined,
      language: typeof navigator !== "undefined" ? navigator.language : undefined,
      visitorId: options.visitorId,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    };

    const res = await fetchJson(`${endpoint}/api/ingest/sessions`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`OrbitTracker: failed to start session (${res.status}) ${text}`);
    }

    const data = (await res.json()) as { sessionId: string };
    sessionId = data.sessionId;
    writeStoredSessionId(sidKey, sessionId);
    logInfo("session started", { sessionId, site, form, page: currentPage, resumed: false });
    options.onSessionStart?.(sessionId);

    // Also drop a custom timeline event so replay shows iframe vs top context.
    pushCustomEvent("orbit-context", {
      url: ctx.url,
      topUrl: ctx.topUrl,
      topOrigin: ctx.topOrigin,
      inIframe: ctx.inIframe,
      site,
      form,
      page: currentPage,
      resumed: false,
    });

    resolveSessionReady();
  }

  /** Attach form/page on the server for an adopted parent session. */
  async function attachFormOnServer(
    formId: string,
    page?: number,
    url?: string,
  ): Promise<boolean> {
    if (!sessionId) return false;
    const body: { form: string; page?: number; url?: string } = { form: formId };
    if (page != null && Number.isFinite(page)) body.page = Math.floor(page);
    if (url) body.url = url;
    const res = await fetchJson(
      `${endpoint}/api/ingest/sessions/${sessionId}/form`,
      {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(body),
      },
    );
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`OrbitTracker: attach form failed (${res.status}) ${text}`);
    }
    return true;
  }

  async function adoptExistingSession(id: string, via: "option" | "storage"): Promise<boolean> {
    try {
      const res = await fetchJson(
        `${endpoint}/api/ingest/sessions/${id}/heartbeat`,
        {
          method: "POST",
          headers: headers(),
          body: "{}",
        },
      );
      if (!res.ok) return false;

      sessionId = id;
      writeStoredSessionId(sidKey, sessionId);

      // Parent handoff: attach form when this frame knows it.
      if (form) {
        const ctx = captureWindowContext();
        await attachFormOnServer(
          form,
          currentPage ?? 1,
          ctx.url,
        );
      }

      logInfo("session started", {
        sessionId,
        site,
        form,
        page: currentPage,
        resumed: true,
        adoptedVia: via,
      });
      options.onSessionStart?.(sessionId);

      const ctx = captureWindowContext();
      pushCustomEvent("orbit-context", {
        url: ctx.url,
        topUrl: ctx.topUrl,
        topOrigin: ctx.topOrigin,
        inIframe: ctx.inIframe,
        site,
        form,
        page: currentPage,
        resumed: true,
        adoptedVia: via,
      });
      resolveSessionReady();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Resume adopted sessionId, then sessionStorage, else create.
   * Adoption lets a form iframe continue the parent page session.
   */
  async function resumeOrCreateSession(): Promise<void> {
    if (adoptSessionId) {
      const ok = await adoptExistingSession(adoptSessionId, "option");
      if (ok) return;
      logInfo("adopt session failed — creating new session", {
        adoptSessionId: adoptSessionId.slice(0, 8),
      });
    }

    const stored = readStoredSessionId(sidKey);
    if (stored) {
      const ok = await adoptExistingSession(stored, "storage");
      if (ok) return;
      clearStoredSessionId(sidKey);
    }

    await createNewSession();
  }

  async function flush(): Promise<void> {
    if (flushing || !sessionId || buffer.length === 0 || stopped) return;
    flushing = true;
    const batch = buffer;
    buffer = [];

    try {
      await postEvents(batch);
    } catch (err) {
      // Put failed events back so the next interval can retry.
      buffer = batch.concat(buffer);
      options.onError?.(err);
    } finally {
      flushing = false;
    }
  }

  function pushCustomEvent(tag: string, payload: Record<string, unknown>) {
    buffer.push({
      type: 5,
      data: { tag, payload },
      timestamp: Date.now(),
    });
    if (buffer.length >= maxBatchSize) {
      void flush();
    }
  }

  async function setForm(
    formId: string,
    page?: number,
    extra?: { url?: string },
  ): Promise<void> {
    const next = String(formId || "").trim();
    if (!next) {
      throw new Error("OrbitTracker: form is required");
    }
    form = next;
    currentPage =
      page != null && Number.isFinite(page) ? Math.floor(page) : currentPage ?? 1;
    const url =
      extra?.url ||
      (typeof location !== "undefined" ? location.href : undefined);

    pushCustomEvent("orbit-form", {
      form,
      page: currentPage,
      site,
      url,
    });

    try {
      await sessionReady;
      if (!sessionId || stopped) return;
      await attachFormOnServer(form, currentPage, url);
      await flush();
    } catch (err) {
      options.onError?.(err);
    }
  }

  async function setPage(page: number, extra?: { url?: string }): Promise<void> {
    if (!form) {
      throw new Error("OrbitTracker: setPage() requires form at init or setForm()");
    }
    if (!Number.isFinite(page) || page < 0) {
      throw new Error("OrbitTracker: page must be a non-negative integer");
    }
    currentPage = Math.floor(page);
    const url =
      extra?.url ||
      (typeof location !== "undefined" ? location.href : undefined);

    pushCustomEvent("orbit-page", {
      page: currentPage,
      form,
      site,
      url,
    });

    try {
      await sessionReady;
      if (!sessionId || stopped) return;
      const res = await fetchJson(`${endpoint}/api/ingest/sessions/${sessionId}/page`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ page: currentPage, url }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`OrbitTracker: setPage failed (${res.status}) ${text}`);
      }
      await flush();
    } catch (err) {
      options.onError?.(err);
    }
  }

  async function trackFormResult(
    outcome: FormResultOutcome,
    detail?: string,
  ): Promise<void> {
    if (!["success", "rejected", "error"].includes(outcome)) {
      throw new Error("OrbitTracker: outcome must be success | rejected | error");
    }

    pushCustomEvent("orbit-form-result", {
      outcome,
      detail,
      form,
      page: currentPage,
      site,
    });

    try {
      await sessionReady;
      if (!sessionId || stopped) return;
      const res = await fetchJson(
        `${endpoint}/api/ingest/sessions/${sessionId}/form-result`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({ outcome, detail }),
        },
      );
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `OrbitTracker: trackFormResult failed (${res.status}) ${text}`,
        );
      }
      await flush();
    } catch (err) {
      options.onError?.(err);
    }
  }

  async function trackEvent(
    name: string,
    payload: Record<string, unknown> = {},
  ): Promise<void> {
    const tag = name.trim() || "orbit-event";
    pushCustomEvent(tag.startsWith("orbit-") ? tag : `orbit-${tag}`, {
      ...payload,
      form,
      page: currentPage,
      site,
    });
    try {
      await flush();
    } catch (err) {
      options.onError?.(err);
    }
  }

  async function trackPortalRequest(
    name: string,
    action: PortalRequestAction,
  ): Promise<void> {
    const requestName = String(name || "").trim();
    if (!requestName) {
      throw new Error("OrbitTracker: portal request name is required");
    }
    if (action !== "viewed" && action !== "completed") {
      throw new Error("OrbitTracker: action must be viewed | completed");
    }

    pushCustomEvent("orbit-portal-request", {
      name: requestName,
      action,
      site,
    });

    try {
      await sessionReady;
      if (!sessionId || stopped) return;
      const res = await fetchJson(
        `${endpoint}/api/ingest/sessions/${sessionId}/portal-request`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({ name: requestName, action }),
        },
      );
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `OrbitTracker: trackPortalRequest failed (${res.status}) ${text}`,
        );
      }
      await flush();
    } catch (err) {
      options.onError?.(err);
    }
  }

  function endSessionOnServer(): void {
    if (!sessionId) return;
    const sid = sessionId;
    const url = `${endpoint}/api/ingest/sessions/${sid}/end?key=${encodeURIComponent(options.apiKey)}`;
    logInfo("session ended", { sessionId: sid, site, form });
    try {
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon(url, new Blob([""], { type: "text/plain" }));
      }
    } catch {
      /* ignore */
    }
    try {
      void fetch(url, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: "",
        credentials: "omit",
        keepalive: true,
        mode: "cors",
      });
    } catch (err) {
      options.onError?.(err);
    }
    clearStoredSessionId(sidKey);
  }

  /**
   * Stop local recording.
   * - Default (page refresh / SPA re-init): flush only — session id stays in sessionStorage.
   * - endSession: true — also end on server and clear storage (explicit stop).
   */
  function stop(opts?: { endSession?: boolean }): void {
    if (stopped) return;
    stopped = true;
    if (flushTimer) clearInterval(flushTimer);
    stopRecording?.();
    void flush();
    if (opts?.endSession) {
      endSessionOnServer();
    }
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("beforeunload", onPageHide);
    document.removeEventListener("visibilitychange", onVisibility);
  }

  function onPageHide() {
    // Refresh and tab close both fire pagehide. We only flush here.
    // sessionStorage keeps the id across refresh; tab close clears it so the
    // next visit starts fresh. Server idle timeout finishes abandoned sessions.
    void flush();
  }

  function onVisibility() {
    if (document.visibilityState === "hidden") {
      void flush();
    }
  }

  void (async () => {
    try {
      await resumeOrCreateSession();
      if (stopped) return;

      stopRecording = record({
        emit(event) {
          if (stopped) return;
          if (options.maskOnlyMarkedInputs) {
            sanitizeRecordedEvent(event, maskedNodeIds);
          }
          buffer.push(event);
          // Full snapshots are large — flush ASAP after the first few events
          // and whenever the batch fills.
          if (buffer.length >= maxBatchSize) {
            void flush();
          } else if (buffer.length === 2) {
            // Typical: Meta + FullSnapshot (and our orbit-context may already be queued)
            void flush();
          }
        },
        maskAllInputs: options.maskAllInputs ?? true,
        maskInputFn: resolveMaskInputFn(options),
        recordCanvas: false,
        collectFonts: true,
        recordCrossOriginIframes: options.recordCrossOriginIframes ?? false,
      }) as unknown as (() => void) | undefined;

      // Push any events already buffered (e.g. orbit-context) immediately.
      void flush();

      flushTimer = setInterval(() => {
        void flush();
      }, flushIntervalMs);

      window.addEventListener("pagehide", onPageHide);
      window.addEventListener("beforeunload", onPageHide);
      document.addEventListener("visibilitychange", onVisibility);
    } catch (err) {
      options.onError?.(err);
      console.error("[OrbitTracker]", err);
      resolveSessionReady();
    }
  })();

  const handle: OrbitTrackerHandle = {
    get sessionId() {
      return sessionId;
    },
    stop,
    flush,
    setForm,
    setPage,
    trackFormResult,
    trackEvent,
    trackPortalRequest,
  };

  activeHandle = handle;
  if (typeof window !== "undefined") {
    window.__orbitTracker = handle;
  }
  return handle;
}

/** Active tracker started by the last `init()` call, or null. */
export function getInstance(): OrbitTrackerHandle | null {
  return activeHandle;
}

const api = { init, getInstance };

// Attach global for script-tag embeds (IIFE / UMD). Does not start recording.
if (typeof window !== "undefined") {
  window.OrbitTracker = api;
  logInfo("loaded — call OrbitTracker.init({ endpoint, apiKey, site, ... }) to start");
}

export default api;
